import { type FC } from "react";
import { Box, IconButton, lighten, Stack, type SxProps, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useIntl } from "react-intl";

import { errorSeverity } from "@/constants";

interface MachineStatusMessageProps {
	readonly message: string;
	readonly severity: (typeof errorSeverity)[keyof typeof errorSeverity];
	readonly onClose?: () => void;
	readonly sx?: SxProps;
}

export const MachineStatusMessage: FC<MachineStatusMessageProps> = ({ message, severity, onClose, sx }) => {
	const theme = useTheme();
	const intl = useIntl();

	const severityColor = severity === errorSeverity.error ? theme.palette.error : theme.palette.warning;
	const severityLabel =
		severity === errorSeverity.error
			? intl.formatMessage({ id: "Error", defaultMessage: "Error" })
			: intl.formatMessage({ id: "Warning", defaultMessage: "Warning" });

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				height: "100%",
				border: 1,
				borderRadius: 1,
				borderColor: severityColor.main,
				backgroundColor: lighten(severityColor.light, 0.8),
				padding: 1,
				...sx,
			}}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
				<Stack alignItems="flex-start">
					<Typography variant="h5">{severityLabel}</Typography>
					<Typography variant="h6">{message}</Typography>
				</Stack>
				{Boolean(onClose) === true && (
					<IconButton size="small" onClick={onClose}>
						<CloseIcon sx={{ fontSize: 20 }} />
					</IconButton>
				)}
			</Stack>
		</Box>
	);
};
